import SettingsSearchTarget from './SettingsSearchTarget';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { StyleSheet, View, Pressable, ActivityIndicator } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { referralApi } from '@/lib/api';
import { trackRetentionEvent, RETENTION_ACTION } from '@/constants/utils/analyticsEvents';
import { useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { formatCreditBalance } from '@/lib/formatCredits';
import { referralProgress, referralText } from '@/lib/referralSharing';
import ReferralShareKit from './ReferralShareKit';
import ReferralProgress from './ReferralProgress';
import ReferralMilestones from './ReferralMilestones';
import moment from 'moment-mini';

const createStyles = (isDark, compact) => {
  const ink = isDark ? '#F5F5F7' : '#1D1D1F';
  const secondary = isDark ? '#A1A1A6' : '#6E6E73';
  const surface = isDark ? '#202022' : '#F5F5F7';
  const line = isDark ? '#38383B' : '#E5E5E7';
  return StyleSheet.create({
    container: { gap: 36, paddingTop: compact ? 16 : 28 },
    loading: { alignItems: 'center', justifyContent: 'center', paddingVertical: 80, gap: 16 },
    hero: { alignItems: 'center', gap: 14 },
    eyebrow: { color: secondary, letterSpacing: 1.7, textTransform: 'uppercase', fontSize: 11 },
    headline: { color: ink, fontSize: compact ? 34 : 44, lineHeight: compact ? 40 : 52, letterSpacing: -1.4, textAlign: 'center' },
    description: { color: secondary, fontSize: 15, lineHeight: 23, textAlign: 'center', maxWidth: 510 },
    rewards: { flexDirection: 'row', width: '100%', maxWidth: 500, marginTop: 10, backgroundColor: surface, borderRadius: 20, paddingVertical: 22 },
    reward: { flex: 1, alignItems: 'center', gap: 5 },
    rewardDivider: { width: 1, backgroundColor: line, marginVertical: 5 },
    rewardNumber: { color: ink, fontSize: 34, lineHeight: 42, letterSpacing: -1 },
    secondary: { color: secondary },
    ink: { color: ink },
    linkSection: { gap: 12 },
    linkCard: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: line, borderRadius: 14, padding: 6, paddingLeft: 16, gap: 12 },
    link: { flex: 1, color: secondary },
    copyButton: { minHeight: 40, paddingHorizontal: 18, justifyContent: 'center', borderRadius: 10, backgroundColor: ink },
    copyLabel: { color: isDark ? '#1D1D1F' : '#FFFFFF' },
    section: { gap: 18 },
    sectionHeading: { fontSize: 20, lineHeight: 26, letterSpacing: -0.4, color: ink },
    stats: { flexDirection: 'row', paddingVertical: 6 },
    stat: { flex: 1, paddingHorizontal: compact ? 8 : 20, gap: 7 },
    statDivider: { borderLeftWidth: 1, borderColor: line },
    statNumber: { fontSize: 28, lineHeight: 34, letterSpacing: -0.7, color: ink },
    statLabel: { color: secondary, lineHeight: 17 },
    history: { borderWidth: 1, borderColor: line, borderRadius: 16, overflow: 'hidden' },
    historyRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 18 },
    historyBorder: { borderTopWidth: 1, borderColor: line },
    historyBody: { flex: 1, gap: 5 },
    badge: { backgroundColor: surface, borderRadius: 7, paddingHorizontal: 8, paddingVertical: 5 },
    empty: { alignItems: 'center', gap: 10, padding: 28, backgroundColor: surface, borderRadius: 16 },
    steps: { gap: 22 },
    step: { flexDirection: 'row', gap: 15, alignItems: 'flex-start' },
    stepNumber: { width: 30, height: 30, borderRadius: 15, backgroundColor: surface, alignItems: 'center', justifyContent: 'center' },
    footer: { borderTopWidth: 1, borderColor: line, paddingTop: 22, gap: 8 },
  });
};

const STATUS_ICONS = { completed: 'check', pending: 'clock', rejected: 'xmark', expired: 'clock' };

export default function ReferralSettings() {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const styles = useMemo(() => createStyles(isDarkTheme(theme), isXS), [theme, isXS]);
  const [isLoading, setIsLoading] = useState(true);
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(false);
      const [codeData, statsData] = await Promise.all([referralApi.getCode(), referralApi.getStats()]);
      setReferralLink(codeData.referralLink || '');
      setStats({ ...codeData.stats, ...statsData.stats });
      setReferrals(statsData.referrals || []);
    } catch (error) {
      setLoadError(true);
      addToast(error.message || t('settings.referral.failedToLoad'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addToast, t]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const copyLink = async () => {
    if (!referralLink) return;
    try {
      await Clipboard.setStringAsync(referralLink);
      trackRetentionEvent(RETENTION_ACTION.REFERRAL_SHARED, 'copy_link');
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2200);
    } catch {
      addToast(t('settings.referral.failedToCopy'), 'error');
    }
  };

  if (isLoading) return <View style={styles.loading}><ActivityIndicator color={styles.secondary.color} /></View>;
  if (loadError) return <View style={styles.loading}>
    <Text accessibilityRole="alert" color="danger">{t('settings.referral.failedToLoad')}</Text>
    <Button text={t('common.retry')} onPress={fetchData} />
  </View>;

  const progress = referralProgress(stats);
  const rewardAvailable = !progress.atCap && !progress.welcomeAtCap && progress.reward > 0;
  const credits = formatCreditBalance(progress.reward);
  const totalEarned = formatCreditBalance(stats.totalCreditsEarned ?? 0);
  const statItems = [
    { label: 'successful', value: progress.completed },
    { label: 'pending', value: stats.totalPending ?? 0 },
    { label: 'creditsEarned', value: totalEarned },
  ];

  return (
    <View style={styles.container}>
      <View style={styles.hero}>
        <Text weight="semibold" style={styles.eyebrow}>{t('settings.referral.eyebrow')}</Text>
        <Text weight="bold" style={styles.headline} accessibilityRole="header" accessibilityLevel={2}>{t('settings.referral.headline')}</Text>
        <Text style={styles.description}>
          {rewardAvailable ? referralText(t, 'heroDescription', { CREDITS: credits }) : t('settings.referral.generalDescription')}
        </Text>
        {rewardAvailable && (
          <View style={styles.rewards} accessibilityLabel={referralText(t, 'rewardHeadline', { CREDITS: credits })}>
            <View style={styles.reward}>
              <Text size="s" style={styles.secondary}>{t('settings.referral.forYourFriend')}</Text>
              <Text weight="bold" style={styles.rewardNumber}>{credits}</Text>
              <Text size="xs" style={styles.secondary}>{t('settings.referral.bonusCredits')}</Text>
            </View>
            <View style={styles.rewardDivider} />
            <View style={styles.reward}>
              <Text size="s" style={styles.secondary}>{t('settings.referral.forYou')}</Text>
              <Text weight="bold" style={styles.rewardNumber}>{credits}</Text>
              <Text size="xs" style={styles.secondary}>{t('settings.referral.bonusCredits')}</Text>
            </View>
          </View>
        )}
        {progress.welcomeAtCap && !progress.atCap && <View style={styles.hero}>
          <Text weight="semibold" style={styles.ink}>{t('settings.referral.signupLimitReached')}</Text>
          <Text style={styles.description}>{t('settings.referral.signupLimitReachedDescription')}</Text>
        </View>}
      </View>

      <View style={styles.linkSection}>
        <SettingsSearchTarget label={t('settings.referral.yourReferralLink')}><Text size="s" weight="semibold" style={styles.ink}>{t('settings.referral.yourReferralLink')}</Text></SettingsSearchTarget>
        <View style={styles.linkCard}>
          <Text size="s" numberOfLines={1} selectable style={styles.link}>{referralLink || t('settings.referral.loading')}</Text>
          <Pressable accessibilityRole="button" accessibilityLabel={t('settings.referral.copyLink')}
            disabled={!referralLink} onPress={copyLink}
            style={({ pressed }) => [styles.copyButton, pressed && { opacity: 0.75 }]}>
            <Text size="s" weight="semibold" style={styles.copyLabel}>{t(copied ? 'settings.referral.copied' : 'settings.referral.copy')}</Text>
          </Pressable>
        </View>
      </View>

      <ReferralMilestones stats={stats} />
      <ReferralShareKit referralLink={referralLink} creditsPerReferral={progress.reward} rewardAvailable={rewardAvailable} />

      <View style={styles.section}>
        <Text weight="bold" style={styles.sectionHeading} accessibilityRole="header" accessibilityLevel={2}>{t('settings.referral.yourProgress')}</Text>
        <View style={styles.stats}>
          {statItems.map((item, index) => (
            <View key={item.label} accessibilityLabel={`${item.value} ${t(`settings.referral.${item.label}`)}`} style={[styles.stat, index > 0 && styles.statDivider]}>
              <Text weight="semibold" style={styles.statNumber}>{item.value}</Text>
              <Text size="xs" style={styles.statLabel}>{t(`settings.referral.${item.label}`)}</Text>
            </View>
          ))}
        </View>
        <ReferralProgress stats={stats} />
      </View>

      <View style={styles.section}>
        <SettingsSearchTarget label={t('settings.referral.referralHistory')}><Text weight="bold" style={styles.sectionHeading} accessibilityRole="header" accessibilityLevel={2}>{t('settings.referral.referralHistory')}</Text></SettingsSearchTarget>
        {referrals.length === 0 ? (
          <View style={styles.empty}>
            <Icon name="user-plus" size={20} color={styles.secondary.color} />
            <Text size="s" style={[styles.secondary, { textAlign: 'center' }]}>{t('settings.referral.noReferrals')}</Text>
          </View>
        ) : (
          <View style={styles.history}>
            {referrals.map((referral, index) => (
              <View key={referral.id} style={[styles.historyRow, index > 0 && styles.historyBorder]}>
                <Icon name={STATUS_ICONS[referral.status] || 'question'} size={14} color={styles.secondary.color} />
                <View style={styles.historyBody}>
                  <Text size="s" weight="medium" style={styles.ink}>{referralText(t, 'referralNumber', { ID: referral.id?.toString().slice(-6) || '—' })}</Text>
                  <Text size="xs" style={styles.secondary}>{referral.createdAt ? moment(referral.createdAt).fromNow() : '—'}</Text>
                  {STATUS_ICONS[referral.status] && <Text size="xs" style={[styles.secondary, { lineHeight: 18 }]}>{t(`settings.referral.statusDescriptions.${referral.status}`)}</Text>}
                </View>
                <View style={styles.badge}>
                  <Text size="xs" weight="medium" style={styles.ink}>{STATUS_ICONS[referral.status] ? t(`settings.referral.statusLabels.${referral.status}`) : '—'}</Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>

      <View style={styles.section}>
        <Text weight="bold" style={styles.sectionHeading} accessibilityRole="header" accessibilityLevel={2}>{t('settings.referral.howItWorks')}</Text>
        <View style={styles.steps}>
          {['share', 'signup', 'earn'].map((step, index) => (
            <View key={step} style={styles.step}>
              <View style={styles.stepNumber}><Text size="s" weight="semibold" style={styles.ink}>{index + 1}</Text></View>
              <View style={styles.historyBody}>
                <Text size="s" weight="semibold" style={styles.ink}>{t(`settings.referral.stepTitles.${step}`)}</Text>
                <Text size="s" style={[styles.secondary, { lineHeight: 20 }]}>
                  {index === 1 && progress.welcomeAtCap ? t('settings.referral.signupLimitReachedDescription')
                    : index === 2 && progress.atCap ? t('settings.referral.limitReachedDescription')
                      : referralText(t, `step${index + 1}`, { CREDITS: credits })}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
      {progress.cap !== null && (
        <View style={styles.footer}>
          <Text size="xs" style={styles.secondary}>{referralText(t, 'maxReferrals', { MAX: progress.cap })}</Text>
        </View>
      )}
    </View>
  );
}
